import type { Dispatch, SetStateAction } from "react"
import type {
  AdminStreakEntry,
  ClaimStreakResult,
  CreateStreakData,
  StreakAnalytics,
  StreakHistoryDay,
  UserStreakEntry,
} from "@/contexts/auth-api/streak-types"
import { streakTreatyClient } from "@/contexts/auth-api/treaty-client"

interface StreakUserShape {
  points: {
    current: number
  }
  freeSpins?: number
}

interface StreaksApiDeps<TUser extends StreakUserShape> {
  getToken: () => string | null
  setUser: Dispatch<SetStateAction<TUser | null>>
}

interface WeeklyRaffleWinner {
  displayName: string
  prizeLabel: string
  cashUsd: number
  isCurrentUser: boolean
}

interface WeeklyRaffleStatus {
  success: boolean
  status: "pending" | "drawn"
  totalQualified: number
  drawTime?: string
  currentUserQualified?: boolean
  qualifiedUsers?: string[]
  winners?: WeeklyRaffleWinner[]
  drawDate?: string
  nextDrawTime?: string
  message?: string
}

interface AdminStreakListResponse {
  success: boolean
  streaks?: AdminStreakEntry[]
  message?: string
}

interface AdminStreakMutationResponse {
  success: boolean
  streak?: unknown
  message?: string
}

interface AdminStreakAnalyticsResponse {
  success: boolean
  analytics?: StreakAnalytics
  message?: string
}

interface UserStreakListResponse {
  success: boolean
  streaks?: UserStreakEntry[]
  message?: string
}

interface ClaimStreakResponse {
  success: boolean
  newStreak?: number
  reward?: ClaimStreakResult["reward"]
  isMilestone?: boolean
  pointsAdded?: number
  message?: string
}

interface StreakHistoryResponse {
  success: boolean
  history?: StreakHistoryDay[]
  currentStreak?: number
  longestStreak?: number
  message?: string
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

function pickTreatyPayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value

  const direct = value as Record<string, unknown>
  if (typeof direct.success === "boolean") return direct

  for (const nested of Object.values(direct)) {
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).success === "boolean") {
      return nested
    }
  }

  return value
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const data = pickTreatyPayload(response.data)
  if (data && typeof data === "object") return data as T

  const errorValue = pickTreatyPayload(response.error?.value)
  if (errorValue && typeof errorValue === "object") {
    return errorValue as T
  }

  return { success: false, message: fallbackMessage } as T
}

export function createStreaksApi<TUser extends StreakUserShape>(deps: StreaksApiDeps<TUser>) {
  const getAdminStreaks = async (): Promise<{ success: boolean; streaks?: AdminStreakEntry[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.admin.streaks.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<AdminStreakListResponse>(response, "Failed to get admin streaks")
      return { success: Boolean(data.success), streaks: data.streaks as AdminStreakEntry[] | undefined, message: data.message }
    } catch (error) {
      console.error("Get admin streaks error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const createAdminStreak = async (data: CreateStreakData): Promise<{ success: boolean; streak?: unknown; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.admin.streaks.post(data, {
        headers: { authorization: `Bearer ${token}` },
      })
      const result = unwrapTreatyResult<AdminStreakMutationResponse>(response, "Failed to create streak")
      return { success: Boolean(result.success), streak: result.streak, message: result.message }
    } catch (error) {
      console.error("Create admin streak error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const updateAdminStreak = async (
    streakId: string,
    data: Partial<CreateStreakData>
  ): Promise<{ success: boolean; streak?: unknown; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.admin.streaks({ streakId }).put(data, {
        headers: { authorization: `Bearer ${token}` },
      })
      const result = unwrapTreatyResult<AdminStreakMutationResponse>(response, "Failed to update streak")
      return { success: Boolean(result.success), streak: result.streak, message: result.message }
    } catch (error) {
      console.error("Update admin streak error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const deleteAdminStreak = async (streakId: string): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.admin.streaks({ streakId }).delete(undefined, {
        headers: { authorization: `Bearer ${token}` },
      })
      const result = unwrapTreatyResult<{ success: boolean; message?: string }>(response, "Failed to delete streak")
      return { success: Boolean(result.success), message: result.message }
    } catch (error) {
      console.error("Delete admin streak error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getAdminStreakAnalytics = async (streakId: string): Promise<{ success: boolean; analytics?: StreakAnalytics; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.admin.streaks({ streakId }).analytics.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<AdminStreakAnalyticsResponse>(response, "Failed to get streak analytics")
      return { success: Boolean(data.success), analytics: data.analytics as StreakAnalytics | undefined, message: data.message }
    } catch (error) {
      console.error("Get admin streak analytics error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getUserStreaks = async (): Promise<{ success: boolean; streaks?: UserStreakEntry[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.streaks.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<UserStreakListResponse>(response, "Failed to get user streaks")
      return { success: Boolean(data.success), streaks: data.streaks as UserStreakEntry[] | undefined, message: data.message }
    } catch (error) {
      console.error("Get user streaks error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const claimStreak = async (streakId: string): Promise<{ success: boolean; result?: ClaimStreakResult; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.streaks({ streakId }).claim.post(undefined, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<ClaimStreakResponse>(response, "Failed to claim streak")

      const pointsAdded = typeof data.pointsAdded === "number" ? data.pointsAdded : 0

      if (data.success && pointsAdded > 0) {
        deps.setUser((prev) => prev ? { ...prev, points: { ...prev.points, current: prev.points.current + pointsAdded } } : prev)
      }

      return {
        success: Boolean(data.success),
        result: data.success &&
          typeof data.newStreak === "number" &&
          !!data.reward &&
          typeof data.isMilestone === "boolean"
          ? {
              newStreak: data.newStreak,
              reward: data.reward,
              isMilestone: data.isMilestone,
              pointsAdded,
            }
          : undefined,
        message: data.message,
      }
    } catch (error) {
      console.error("Claim streak error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getStreakHistory = async (
    streakId: string
  ): Promise<{ success: boolean; history?: StreakHistoryDay[]; currentStreak?: number; longestStreak?: number; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.streaks({ streakId }).history.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<StreakHistoryResponse>(response, "Failed to get streak history")
      return {
        success: Boolean(data.success),
        history: data.history as StreakHistoryDay[] | undefined,
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        message: data.message,
      }
    } catch (error) {
      console.error("Get streak history error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const markStreakSeen = async (streakId: string): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await streakTreatyClient.api.streaks({ streakId }).seen.post(undefined, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{ success: boolean; message?: string }>(response, "Failed to mark streak as seen")
      return { success: Boolean(data.success), message: data.message }
    } catch (error) {
      console.error("Mark streak seen error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getWeeklyRaffleStatus = async (): Promise<WeeklyRaffleStatus> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, status: "pending", totalQualified: 0, message: "Not authenticated" }
    }

    try {
      const response = await streakTreatyClient.api.raffle["weekly-status"].get({
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<WeeklyRaffleStatus>(response, "Network error")
    } catch (error) {
      console.error("Get weekly raffle status error:", error)
      return { success: false, status: "pending", totalQualified: 0, message: "Network error" }
    }
  }

  return {
    getAdminStreaks,
    createAdminStreak,
    updateAdminStreak,
    deleteAdminStreak,
    getAdminStreakAnalytics,
    getUserStreaks,
    claimStreak,
    getStreakHistory,
    markStreakSeen,
    getWeeklyRaffleStatus,
  }
}
